// lib
import React, { useContext, useRef, useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'

import ADND from '@aytm/lib/adnd/ADND'
import { DataContext } from '@aytm/app/stats/page/question-views/components/mapping/Component'

import { generateUID, plural } from '@aytm/app/stats/lib/helpers'

// components
import Entity from '@aytm/app/stats/page/question-views/components/mapping/Entity'

// styles
import style from '@aytm/app/stats/page/question-views/components/mapping/styles/Component.module.scss'

const getDefaultEntity = (item = {}) => {
  return {
    key: generateUID(),
    text: '',
    pattern: '',
    ...item
  }
}

const onDNDPositionChange = ({ newPosition, dragItemUpdater } = {}) =>
  dragItemUpdater(node => {
    const itemPositionNode = node.querySelector(`.stats-Concept-Item-index`)
    if (null !== itemPositionNode) {
      itemPositionNode.innerHTML = null === newPosition ? '' : `${newPosition + 1}.`
    }
  })

const Entities = ({ items, imagesDisabled, onReset }) => {
  const { id, mode, data, original, disabled, dispatch } = useContext(DataContext)

  const dndItemsListRef = useRef()

  const textLabel = useMemo(() => (imagesDisabled ? 'Label' : 'Label & Image'), [imagesDisabled])

  const showReset = useMemo(() => !original && !disabled && 'function' == typeof onReset, [original, disabled, onReset])

  const onDNDUpdateItemPositions = ({ hiddenItemIndex, placeholderIndex } = {}) => {
    const itemPositionNodes = dndItemsListRef.current.querySelectorAll(`.stats-Concept-Item .stats-Concept-Item-index`)

    for (const [i, itemPositionNode] of itemPositionNodes.entries()) {
      if (null !== itemPositionNode) {
        const visualItemIndex = i + (null !== hiddenItemIndex && hiddenItemIndex <= i ? -1 : 0)
        const newItemIndex =
          visualItemIndex + (null !== placeholderIndex && placeholderIndex <= visualItemIndex ? 1 : 0)
        itemPositionNode.innerHTML = null === newItemIndex ? '' : `${newItemIndex + 1}.`
      }
    }
  }

  const onDNDDrop = res => {
    const { item, position, clone } = res

    if (clone) {
      dispatch({
        type: 'ADD_ENTITY',
        data: {
          item: {
            ...getDefaultEntity(item.item),
            pattern: '',
            key: generateUID()
          },
          position
        }
      })
    } else if (item.id != id) {
      item.remove()
      dispatch({
        type: 'ADD_ENTITY',
        data: {
          item: {
            ...getDefaultEntity(item.item),
            pattern: '',
            key: generateUID()
          },
          position
        }
      })
    } else if (item.position != position) {
      dispatch({
        type: 'REORDER_ENTITIES',
        data: {
          from: item.position,
          to: position
        }
      })
    }

    onDNDUpdateItemPositions()
  }

  const dndItemsList = ADND.targets
    .itemsList(
      {
        acceptTypes: [`dlq-entity-${data.windowUID}`],
        placeholderVMargin: 10
      },
      { capture: false }
    )
    .buildPlaceholder(() => {
      const placeholderNode = document.createElement('div')
      placeholderNode.className = `${style.placeholder}`
      return placeholderNode
    })
    .onPositionChange(onDNDPositionChange)
    .onUpdateItemPositions(onDNDUpdateItemPositions)
    .onEnter(onDNDUpdateItemPositions)
    .onLeave(onDNDUpdateItemPositions)
    .onDrop(onDNDDrop)

  const onAddClick = () => {
    dispatch({
      type: 'ADD_ENTITY',
      data: {
        item: getDefaultEntity(),
        position: items.length
      }
    })
  }

  useEffect(() => {
    dndItemsList.connect(dndItemsListRef.current)

    return () => {
      dndItemsList.disconnect()
    }
  }, [dndItemsList])

  return (
    <div className={style.component}>
      <div className={`${style.row} ${style['row-header']} p-fluid p-col-12`}>
        {showReset ? (
          <>
            <div className="p-field p-col-4">
              <b className={style.title}>
                {items.length} {plural(items.length, 'entity', 'entities')}
              </b>
            </div>
            <div className="p-field p-right p-col-8">
              <span className={style.toggle} onClick={onReset}>
                reset
              </span>
            </div>
          </>
        ) : (
          <b className={style.title}>
            {items.length} {plural(items.length, 'entity', 'entities')}
          </b>
        )}
      </div>
      <div
        className={`${style.row} ${style['row-label']}${
          disabled ? ' ' + style['row-label-disabled'] : ''
        } p-fluid p-col-12`}
      >
        {disabled || 'advanced' == mode ? (
          <div className="p-field p-col-12">{textLabel}</div>
        ) : (
          <>
            <div className="p-field p-col-4">Data source</div>
            <div className="p-field p-col-8">{textLabel}</div>
          </>
        )}
      </div>
      <div ref={dndItemsListRef} className={style.entities}>
        {items.map((item, index) => (
          <Entity
            key={item.key}
            item={item}
            position={index + 1}
            imagesDisabled={imagesDisabled}
            dndItemsList={dndItemsList}
          />
        ))}
      </div>
      {disabled ? null : (
        <div className={`${style.row} ${style['row-add']} p-fluid p-col-12`}>
          <div className={`${style.index} p-field`}>{items.length + 1}.</div>
          <div className="p-field">
            <span className={style.add} onClick={onAddClick}>
              Add an entity
            </span>
          </div>
        </div>
      )}
    </div>
  )
}

Entities.propTypes = {
  items: PropTypes.array,
  imagesDisabled: PropTypes.bool,
  onReset: PropTypes.func
}

Entities.displayName = 'Entities'

export default Entities

ADND().connect()
